import { Box, Divider, Link, Typography, useTheme } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { IAiSearchResultResponse } from '../../shared/interfaces/IAiSearchResultResponse';
import Helper from '../../shared/helpers/Helper';
import { customColors } from '../../constants/theme';
import NoContent from '../../assets/images/research_no_content.png';

interface Props {
  // undefined / null means no result row exists yet (pending), an object is a stored result.
  searchResult?: IAiSearchResultResponse | null;
}

// The AI "sparkle" icon is a golden yellow in the mockup.
const AI_ICON_COLOR = '#F5B301';

const AiSearchResult = ({ searchResult }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const outerCardSx = {
    m: theme.spacing(2),
    p: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: `rgba(${customColors.primary.main}, 0.1)`,
  };

  const innerCardSx = {
    p: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  };

  // No stored result yet: the crawl has not been ingested. This is distinct from a
  // stored-but-empty result (empty === true), which means the crawl returned no overview.
  if (!searchResult) {
    return (
      <Box sx={outerCardSx}>
        <Box sx={innerCardSx}>
          <Typography sx={{ textAlign: 'center', py: theme.spacing(2) }}>
            {t('ai_search_pending')}
          </Typography>
        </Box>
      </Box>
    );
  }

  const { overview } = searchResult;
  const isEmpty = searchResult.empty || !overview;

  return (
    <Box sx={outerCardSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: theme.spacing(2),
        }}
      >
        {isEmpty ? (
          <span />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AutoAwesomeIcon sx={{ color: AI_ICON_COLOR, mr: theme.spacing(1) }} />
            <Typography variant="h6">{t('ai_search_answer')}</Typography>
          </Box>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
        >
          {t('ai_search_last_update')}: {Helper.getFormattedDate(searchResult.scrapedAt, true)}
        </Typography>
      </Box>

      <Box sx={innerCardSx}>
        {isEmpty ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: theme.spacing(2),
            }}
          >
            <img
              src={NoContent}
              alt="No content"
              style={{ width: '100%', maxWidth: '220px', height: 'auto' }}
            />
            <Typography
              variant="body1"
              color={theme.palette.primary.main}
              fontWeight="bolder"
              sx={{ mt: theme.spacing(1) }}
            >
              {t('ai_search_empty')}
            </Typography>
          </Box>
        ) : (
          <>
            {(overview?.ai_overview_contents ?? []).map((block, index) => {
              if (block.type === 'header') {
                return (
                  <Typography
                    key={index}
                    variant="subtitle1"
                    sx={{ mt: theme.spacing(2), fontWeight: 700 }}
                  >
                    {block.text}
                  </Typography>
                );
              }
              if (block.type === 'list') {
                return (
                  <Box
                    component="ul"
                    key={index}
                    sx={{ my: theme.spacing(1) }}
                  >
                    {block.list.map((item, itemIndex) => (
                      <li key={itemIndex}>{item.text}</li>
                    ))}
                  </Box>
                );
              }
              return (
                <Typography
                  key={index}
                  sx={{ mt: theme.spacing(1) }}
                >
                  {block.text}
                </Typography>
              );
            })}
            {(overview?.ai_overview_sources ?? []).length > 0 && (
              <Box sx={{ mt: theme.spacing(2) }}>
                <Divider sx={{ mb: theme.spacing(1) }} />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: theme.spacing(1) }}
                >
                  {t('ai_search_sources')}
                </Typography>
                {(overview?.ai_overview_sources ?? []).map((source, index) => (
                  <Link
                    key={`${source.source_url}-${index}`}
                    href={source.source_url}
                    target="_blank"
                    rel="noreferrer"
                    display="block"
                  >
                    {source.source_name || source.source_domain || source.source_url}
                  </Link>
                ))}
              </Box>
            )}

            <Typography
              variant="caption"
              display="block"
              sx={{ mt: theme.spacing(2) }}
            >
              {overview?.ai_overview_footer
                ? overview.ai_overview_footer
                : t('ai_disclaimer_content')}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AiSearchResult;
